import { CompatClient } from '@stomp/stompjs';
import { DEFAULT_SOUNDTRACK_VOLUME_LEVEL } from '../../constants/app-constants';
import { UserProfile } from '../../core/models/user-profile';

export type WsSubscriptionData = {
  id: string;
  resubscribeAction: any;
};

export type NavbarState =
  | 'HIDDEN'
  | 'VISIBLE_PROSPECT_MODE'
  | 'VISIBLE_PLAIN'
  | 'VISIBLE_WITH_VOLUME_SLIDER'
  | 'VISIBLE_WITH_NAVIGATION_OPTIONS'
  | 'HEALER_SIDE_NAVBAR';
export type NavigationOption = 'HOME' | 'BOOKED_APPOINTMENTS' | 'MY_HEALERS' | 'SETTINGS' | 'CONTACT_US' | 'REQUEST_NEW_HEALER' | 'MY_SELECTIONS';

export interface GlobalState {
  isHealer: boolean;
  isMobileUser: boolean;
  screenOrientation: 'PORTRAIT' | 'LANDSCAPE';
  hasInteracted: boolean;
  navbarState: NavbarState;
  currentUser?: UserProfile;
  ws: {
    subscriptions: {
      musicSubscription?: WsSubscriptionData;
      healloSubscription?: WsSubscriptionData;
    };
    websocketClient?: CompatClient;
    connected: boolean;
  };
  currentUrl?: string;
  musicPlayer: {
    musicEnabled: boolean;
    selectedSoundtrack?: string;
    soundtrackVolume: number;
  };
}

export const initialGlobalState = () => {
  return {
    isHealer: false,
    hasInteracted: false,
    isMobileUser: false,
    navbarState: 'HIDDEN',
    screenOrientation: 'PORTRAIT',
    ws: {
      connected: false,
      subscriptions: [],
    },
    musicPlayer: {
      soundtrackVolume: DEFAULT_SOUNDTRACK_VOLUME_LEVEL,
      musicEnabled: false,
    },
  } as GlobalState;
};
export interface GlobalAction {
  type:
    | 'SET_CURRENT_USER_ID'
    | 'SET_CURRENT_USER'
    | 'SET_USER_HAS_INTERACTED'
    | 'SET_IS_HEALER'
    | 'SET_WS_CONNECTED'
    | 'SET_CURRENT_URL'
    | 'SET_IS_MOBILE_USER'
    | 'SET_SCREEN_ORIENTATION'
    | 'SHOW_NAVBAR'
    | 'HIDE_NAVBAR'
    | 'SET_SELECTED_SOUNDTRACK'
    | 'SET_SOUNDTRACK_VOLUME_LEVEL'
    | 'SET_MUSIC_ENABLED'
    | 'SET_MUSIC_SUBSCRIPTION'
    | 'SET_HEALLO_SUBSCRIPTION'
    | 'SET_WS_CLIENT'
    | 'HEALER_LOGGED_OUT';
  payload?: any;
}

const globalReducer = (state: GlobalState = initialGlobalState(), action: GlobalAction): GlobalState => {
  switch (action.type) {
    case 'SET_CURRENT_USER_ID': {
      return { ...state, currentUser: { ...state.currentUser, id: action.payload } as UserProfile };
    }
    case 'SET_CURRENT_USER': {
      return { ...state, currentUser: action.payload as UserProfile };
    }
    case 'SET_WS_CONNECTED': {
      return { ...state, ws: { ...state.ws, connected: action.payload } };
    }
    case 'SET_IS_HEALER': {
      return { ...state, isHealer: action.payload };
    }
    case 'SET_USER_HAS_INTERACTED': {
      return { ...state, hasInteracted: action.payload };
    }
    case 'SET_CURRENT_URL': {
      return { ...state, currentUrl: action.payload };
    }
    case 'SET_IS_MOBILE_USER': {
      return { ...state, isMobileUser: action.payload };
    }
    case 'SET_SCREEN_ORIENTATION': {
      return { ...state, screenOrientation: action.payload };
    }
    case 'SHOW_NAVBAR': {
      return { ...state, navbarState: action.payload };
    }
    case 'HIDE_NAVBAR': {
      return {
        ...state,
        navbarState: 'HIDDEN',
        musicPlayer: {
          ...state.musicPlayer,
          musicEnabled: false,
          soundtrackVolume: DEFAULT_SOUNDTRACK_VOLUME_LEVEL,
          selectedSoundtrack: undefined,
        },
      };
    }
    case 'SET_MUSIC_ENABLED': {
      return { ...state, musicPlayer: { ...state.musicPlayer, musicEnabled: action.payload } };
    }
    case 'SET_SOUNDTRACK_VOLUME_LEVEL': {
      return { ...state, musicPlayer: { ...state.musicPlayer, soundtrackVolume: action.payload != null ? action.payload : DEFAULT_SOUNDTRACK_VOLUME_LEVEL } };
    }
    case 'SET_SELECTED_SOUNDTRACK': {
      if (state.musicPlayer.selectedSoundtrack === action.payload) {
        return { ...state };
      }
      return { ...state, musicPlayer: { ...state.musicPlayer, selectedSoundtrack: action.payload } };
    }
    case 'SET_MUSIC_SUBSCRIPTION': {
      return { ...state, ws: { ...state.ws, subscriptions: { ...state.ws.subscriptions, musicSubscription: action.payload } } };
    }
    case 'SET_HEALLO_SUBSCRIPTION':
      return { ...state, ws: { ...state.ws, subscriptions: { ...state.ws.subscriptions, healloSubscription: action.payload } } };
    case 'SET_WS_CLIENT': {
      return { ...state, ws: { ...state.ws, websocketClient: action.payload } };
    }
    case 'HEALER_LOGGED_OUT': {
      return initialGlobalState();
    }
    default:
      return state;
  }
};

export default globalReducer;
